import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  CreateOrUpdateTextProjectSettingCommand,
  TextProjectSettingsClient,
  ITextProjectSettingsClient,
  VariableStemDto,
  TextProjectSettingDto,
} from "app/ApiClient";
import { LoadingState } from "types";
import { LanguageVariableState, initialLanguageVariableState } from "./languageVariableState";
import { getStems } from "slices/stems/stemsSlice";

const textProjectSettingsClient: ITextProjectSettingsClient = new TextProjectSettingsClient(
  process.env.REACT_APP_API_URL,
);
const LanguageVariablePattern = /^(DV_Language|DV_LangOverlay|L2)$/i;

export const createLanguageVariable = createAsyncThunk(
  "languageVariables/createLanguageVariable",
  async (data: CreateOrUpdateTextProjectSettingCommand, { dispatch }) => {
    const result = await textProjectSettingsClient.createOrUpdateTextProjectSetting({ ...data });
    if (result)
      dispatch(
        getLanguageVariableByProjectId({
          projectId: data.projectId ?? 0,
        }),
      );
    return result;
  },
);

export const getLanguageVariableByProjectId = createAsyncThunk(
  "languageVariables/getLanguageVariableByProjectId",
  async (data: { projectId: number }) => {
    const result = await textProjectSettingsClient.getTextProjectSettingByProjectId(data.projectId);
    return result;
  },
);

export const detectLanguageVariable = createAsyncThunk(
  "languageVariables/detectLanguageVariable",
  async (_args, { dispatch, getState, rejectWithValue }) => {
    const {
      languageVariable: { projectId, encodedVersionId },
    } = getState() as {
      languageVariable: LanguageVariableState;
    };

    let languageVariableName: string | undefined;
    let stems: VariableStemDto[] = [];
    let textProjectSetting: TextProjectSettingDto | undefined;
    if (projectId === 0 || !encodedVersionId) {
      return { languageVariableName, textProjectSetting };
    }
    try {
      if (projectId && encodedVersionId) {
        textProjectSetting = await textProjectSettingsClient.getTextProjectSettingByProjectId(projectId);
        if (textProjectSetting?.languageVariableName) {
          languageVariableName = textProjectSetting?.languageVariableName;
        }

        if (!languageVariableName) {
          const { payload } = await dispatch(getStems({ projectId, versionId: encodedVersionId ?? "" }));
          stems = payload as VariableStemDto[];
          if (stems && stems.length > 0) {
            languageVariableName = stems.find((stem) => LanguageVariablePattern.test(stem.stem))?.stem;
          }
        }
      }
      return { languageVariableName, textProjectSetting };
    } catch (err) {
      console.error(err);
      rejectWithValue(err);
      return null;
    }
  },
);

export const setProjectId = createAction<{ projectId: number; encodedVersionId: string }>(
  "languageVariables/setProjectId",
);

const languageVariableSlice = createSlice({
  name: "languageVariable",
  initialState: initialLanguageVariableState,
  reducers: {
    resetLanguageVariable: () => initialLanguageVariableState,
    resetCreateLanguageVariableState: (state) => {
      state.createLanguageVariableLoadingState = LoadingState.idle;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createLanguageVariable.pending, (state) => {
      state.createLanguageVariableLoadingState = LoadingState.pending;
    });
    builder.addCase(createLanguageVariable.fulfilled, (state) => {
      state.createLanguageVariableLoadingState = LoadingState.succeed;
    });
    builder.addCase(createLanguageVariable.rejected, (state) => {
      state.createLanguageVariableLoadingState = LoadingState.failed;
    });

    builder.addCase(getLanguageVariableByProjectId.pending, (state) => {
      state.languageVariableLoadingState = LoadingState.pending;
    });
    builder.addCase(getLanguageVariableByProjectId.fulfilled, (state, { payload }) => {
      state.languageVariableLoadingState = LoadingState.succeed;
      state.textProjectSetting = payload ?? null;
      state.languageVariableName = payload ? payload.languageVariableName : undefined;
    });
    builder.addCase(getLanguageVariableByProjectId.rejected, (state) => {
      state.languageVariableLoadingState = LoadingState.failed;
      state.textProjectSetting = null;
    });

    builder.addCase(setProjectId, (state, { payload }) => {
      if (state.projectId !== payload.projectId || state.encodedVersionId !== payload.encodedVersionId) {
        state.languageDetectionStatus = LoadingState.idle;
        state.languageVariableName = undefined;
        state.textProjectSetting = null;
      }
      state.projectId = payload.projectId;
      state.encodedVersionId = payload.encodedVersionId;
    });

    builder.addCase(detectLanguageVariable.pending, (state) => {
      state.languageDetectionStatus = LoadingState.pending;
    });

    builder.addCase(detectLanguageVariable.fulfilled, (state, { payload }) => {
      state.languageDetectionStatus = LoadingState.succeed;
      if (payload) {
        state.textProjectSetting = payload.textProjectSetting ?? null;
        state.languageVariableName = payload.languageVariableName;
      }
    });

    builder.addCase(detectLanguageVariable.rejected, (state) => {
      state.languageDetectionStatus = LoadingState.failed;
    });
  },
});

export const { resetCreateLanguageVariableState, resetLanguageVariable } = languageVariableSlice.actions;
export default languageVariableSlice.reducer;
